import React, { useEffect, useState, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import Slider from '@react-native-community/slider';

// Load WaveSurfer on web from CDN at runtime to avoid bundling/install issues
const loadWaveSurferFromCDN = () => new Promise((resolve, reject) => {
  if (typeof window !== 'undefined' && window.WaveSurfer) {
    return resolve(window.WaveSurfer);
  }
  if (typeof document === 'undefined') {
    return reject(new Error('Document not available'));
  }
  const script = document.createElement('script');
  script.src = 'https://unpkg.com/wavesurfer.js@7/dist/wavesurfer.min.js';
  script.async = true;
  script.onload = () => resolve(window.WaveSurfer);
  script.onerror = () => reject(new Error('Failed to load WaveSurfer from CDN'));
  document.head.appendChild(script);
});

export default function AudioPlayer({ url, isTabOpen = false, autoPlay = false }) {
  const containerRef = useRef(null);
  const waveSurferRef = useRef(null);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    console.log('AudioPlayer URL:', url);

    let cancelled = false;

    if (Platform.OS === 'web' && containerRef.current) {
      (async () => {
        try {
          const WaveSurferLib = await loadWaveSurferFromCDN();
          if (cancelled) return;

          const waveSurfer = WaveSurferLib.create({
            container: containerRef.current,
            waveColor: "#ddd",
            progressColor: "#4a90e2",
            cursorColor: "#333",
            height: 80,
            responsive: true,
            normalize: true,
            backend: 'WebAudio',
            mediaControls: false,
          });

          waveSurfer.load(url);
          waveSurferRef.current = waveSurfer;

          waveSurfer.on('ready', () => {
            setDuration(waveSurfer.getDuration());
            if (autoPlay) {
              console.log('Auto-playing audio');
              waveSurfer.play();
            }
          });
          waveSurfer.on('play', () => setIsPlaying(true));
          waveSurfer.on('pause', () => setIsPlaying(false));
          waveSurfer.on('finish', () => setIsPlaying(false));
          waveSurfer.on('audioprocess', () => {
            setCurrentTime(waveSurfer.getCurrentTime());
          });
        } catch (e) {
          console.log('Failed to initialize WaveSurfer:', e);
        }
      })();
    } else {
      // Mobile fallback - simple audio player
      const audio = new Audio(url);
      audioRef.current = audio;

      const onLoaded = () => setDuration(audio.duration);
      const onTimeUpdate = () => setCurrentTime(audio.currentTime);
      const onEnded = () => setIsPlaying(false);

      audio.addEventListener('loadedmetadata', onLoaded);
      audio.addEventListener('timeupdate', onTimeUpdate);
      audio.addEventListener('ended', onEnded);

      if (autoPlay) {
        audio.addEventListener('loadedmetadata', () => {
          console.log('Auto-playing audio on mobile');
          audio.play().then(() => setIsPlaying(true)).catch(err => {
            console.log('Auto-play failed:', err);
          });
        });
      }
    }

    return () => {
      cancelled = true;
      if (Platform.OS === 'web') {
        if (waveSurferRef.current) {
          try { waveSurferRef.current.pause(); } catch (e) {}
          waveSurferRef.current.destroy();
          waveSurferRef.current = null;
        }
      } else {
        if (audioRef.current) {
          try { audioRef.current.pause(); } catch (e) {}
          try { audioRef.current.remove(); } catch (e) {}
          audioRef.current = null;
        }
      }
    };
  },[url, autoPlay]);


  // Additional effect to handle browser tab visibility changes
  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleVisibilityChange = () => {
        if (document.hidden && isPlaying) {
          console.log('Browser tab became hidden, pausing audio');
          if (waveSurferRef.current) {
            waveSurferRef.current.pause();
          }
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [isPlaying]);

  const togglePlay = () => {
    if (Platform.OS === 'web') {
      // Web WaveSurfer handling
      if (waveSurferRef.current) {
        waveSurferRef.current.playPause();
      }
    } else {
      // Mobile audio handling
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          audioRef.current.play();
          setIsPlaying(true);
        }
      }
    }
  };

  const seek = (value) => {
    if (Platform.OS === 'web') {
      // Web WaveSurfer seeking
      if (waveSurferRef.current) {
        waveSurferRef.current.seekTo(value / duration);
      }
    } else {
      // Mobile audio seeking
      if (audioRef.current) {
        audioRef.current.currentTime = value;
        setCurrentTime(value);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Effect to pause audio when tab becomes inactive
  useEffect(() => {
    if (!isTabOpen && isPlaying) {
      console.log('Tab became inactive, pausing audio');
      if (Platform.OS === 'web') {
        if (waveSurferRef.current) {
          waveSurferRef.current.pause();
        }
      } else {
        if (audioRef.current) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
      }
    }
  }, [isTabOpen, isPlaying]);

  // Cleanup effect to pause audio when component unmounts
  useEffect(() => {
    return () => {
      console.log('AudioPlayer unmounting, pausing audio');
      if (Platform.OS === 'web') {
        if (waveSurferRef.current) {
          waveSurferRef.current.pause();
        }
      } else {
        if (audioRef.current) {
          audioRef.current.pause();
        }
      }
    };
  }, []);

  if (!isTabOpen) {
    return null;
  }

  return (
    <View style={styles.container}>
      {Platform.OS === 'web' ? (
        // Web with WaveSurfer
        <>
          <View ref={containerRef} style={styles.waveformContainer} />
          <View style={styles.controls}>
            <TouchableOpacity style={styles.button} onPress={togglePlay}>
              <Text style={styles.buttonText}>
                {isPlaying ? 'Pause' : 'Play'}
              </Text>
            </TouchableOpacity>
            <Text style={styles.time}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </Text>
          </View>
        </>
      ) : (
        // Mobile with slider
        <>
          <TouchableOpacity style={styles.button} onPress={togglePlay}>
            <Text style={styles.buttonText}>
              {isPlaying ? 'Pause' : 'Play'}
            </Text>
          </TouchableOpacity>

          <Slider
            style={{ flex: 1 }}
            minimumValue={0}
            maximumValue={duration || 0}
            value={currentTime || 0}
            onSlidingComplete={seek}
            minimumTrackTintColor="#4a90e2"
            maximumTrackTintColor="#888"
            thumbTintColor="#fff"
          />

          <Text style={styles.time}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </Text>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 10,
    backgroundColor: '#222',
    borderRadius: 8,
    width: '100%',
  },
  waveformContainer: {
    width: '100%',
    height: 80,
    marginBottom: 10,
  },
  controls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  button: {
    padding: 10,
    backgroundColor: '#4a90e2',
    borderRadius: 6,
  },
  buttonText: {
    color: '#fff',
    fontWeight: '600',
  },
  time: {
    color: '#fff',
    fontSize: 14,
  },
});
